import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  DollarSign, 
  TrendingUp, 
  Zap, 
  CheckCircle2,
  ArrowRight,
  Users,
  Calendar,
  Shield,
  Sparkles,
  Heart,
  Globe,
  Clock,
  Calculator,
  PlayCircle
} from 'lucide-react';

const LiveStreamRevShare = () => {
  const howItWorksSteps = [
    {
      step: "1",
      title: "You Set Up the Stream",
      description: "Create a live stream for any event—recital, meet, showcase, tournament.",
      icon: PlayCircle,
      details: ["One-click event creation", "Automatic quality optimization", "Branded event pages"]
    },
    {
      step: "2", 
      title: "Families Buy Digital Tickets",
      description: "Families purchase digital tickets to watch live or on-demand.",
      icon: Users,
      details: ["Secure payment processing", "Instant access delivery", "Multi-device viewing"]
    },
    {
      step: "3",
      title: "We Handle Everything",
      description: "Ticketing, secure streaming, access control, and payouts—all automated.",
      icon: Shield,
      details: ["Military-grade security", "Global streaming delivery", "Automatic payouts"]
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Grandma Watches From Across the Country",
      description: "Connect families worldwide with professional-quality streaming"
    },
    {
      icon: Clock,
      title: "Parents Never Miss a Moment",
      description: "Even when they can't attend, families stay connected to every performance"
    },
    {
      icon: DollarSign,
      title: "You Create Experiences, We Send Checks",
      description: "Focus on amazing events while we handle the revenue generation"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white mb-6 px-8 py-3 text-xl font-bold animate-pulse">
            <Sparkles className="h-6 w-6 mr-2" />
            GAME CHANGER
          </Badge>
          
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Unlock New Revenue:
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent block">
              Stream Events, Share the Profits
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-gray-700 max-w-4xl mx-auto mb-8 font-medium">
            Let Families Watch From Anywhere—
            <span className="font-bold text-green-700">You Earn on Every Stream</span>
          </p>

          {/* Revenue Highlight Box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-3xl p-8 max-w-3xl mx-auto shadow-2xl"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              💰 Keep 50% of All Streaming Revenue
            </h3>
            <p className="text-xl text-green-100 mb-6">
              Every ticket, every event—automatically deposited to your account
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold mb-2">$0</div>
                <div className="text-green-100">Setup Fees</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold mb-2">$0</div>
                <div className="text-green-100">Tech Headaches</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold mb-2">$0</div>
                <div className="text-green-100">Risk</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h3>
            <p className="text-xl text-gray-600">
              Three simple steps to start earning revenue from every event
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                {/* Step Connector Line */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-green-300 to-transparent z-0" />
                )}
                
                <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 border-green-100 hover:border-green-200 relative z-10">
                  <CardContent className="p-8 text-center">
                    {/* Step Number */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-6">
                      {step.step}
                    </div>
                    
                    {/* Icon */}
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl p-4 w-fit mx-auto mb-6">
                      <step.icon className="h-8 w-8 text-green-600" />
                    </div>
                    
                    <h4 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h4>
                    <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>
                    
                    {/* Details */}
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* You Earn Effortlessly Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-12 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-8">
              You Earn, Effortlessly
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <TrendingUp className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold mb-3">Keep 50% of Revenue</h4>
                <p className="text-gray-300">Every ticket, every event—automatically yours</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <Zap className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold mb-3">Zero Setup Fees</h4>
                <p className="text-gray-300">No upfront costs, no tech headaches, no risk</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <Calendar className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold mb-3">Real-Time Dashboard</h4>
                <p className="text-gray-300">Watch your earnings grow with every stream</p>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 max-w-4xl mx-auto">
              <h4 className="text-2xl font-bold mb-6 flex items-center justify-center">
                <Calculator className="h-8 w-8 mr-3" />
                Revenue Example
              </h4>
              
              <div className="grid md:grid-cols-4 gap-6 text-center mb-6">
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="text-3xl font-bold">40</div>
                  <div className="text-green-100">Families</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="text-3xl font-bold">$12</div>
                  <div className="text-green-100">Per Ticket</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4">
                  <div className="text-3xl font-bold">$480</div>
                  <div className="text-green-100">Gross Revenue</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4 border-2 border-yellow-300">
                  <div className="text-3xl font-bold text-yellow-300">$240</div>
                  <div className="text-yellow-200">You Earn</div>
                </div>
              </div>
              
              <p className="text-xl text-green-100 font-medium">
                For every event, automatically—deposited within 2 business days
              </p>
            </div>
          </div>
        </motion.div>

        {/* Why Partner Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner With HiveShare?
            </h3>
            <p className="text-xl text-gray-600">
              We handle the technology, you focus on creating amazing experiences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 text-center">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-4 w-fit mx-auto mb-6">
                      <benefit.icon className="h-10 w-10 text-blue-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-12 text-white"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Turn Your Next Event Into a Revenue Stream?
          </h3>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Book a demo, claim your spot, and start earning in minutes. 
            <span className="font-bold"> Your first event revenue share starts immediately.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 px-10 py-6 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="h-6 w-6 mr-3" />
              Book Demo & Start Earning
              <ArrowRight className="h-6 w-6 ml-3" />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-3 border-white text-white hover:bg-white hover:text-green-600 px-10 py-6 text-xl font-bold transition-all duration-300"
            >
              <Globe className="h-6 w-6 mr-3" />
              Claim Your Revenue Share
            </Button>
          </div>
          
          <p className="text-green-100 mt-6 text-lg">
            ⚡ Setup in 10 minutes • 💰 First payout within 48 hours • 🎯 Zero risk guarantee
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamRevShare;